import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getClients, removeClient, getClientProgress, createCoachInvite, getCoachInvites, deleteCoachInvite } from './coachApi';

const TIER_LIMITS = { solo: 15, club: 75, pro: Infinity, elite: Infinity };

function StatPill({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 52 }}>
      <div style={{ color: color || _T.accent, fontSize: 14, fontWeight: 700 }}>{value ?? '—'}</div>
      <div style={{ color: C.silver2, fontSize: 9, marginTop: 1 }}>{label}</div>
    </div>
  );
}

function ClientDetailView({ client, coachId, nl, onBack }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    getClientProgress(coachId, client.user_id).then(d => { setProgress(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId, client.user_id]);

  const avg = (arr, key) => {
    if (!arr?.length) return null;
    return Math.round(arr.reduce((s, x) => s + (x[key] || 0), 0) / arr.length);
  };

  const sleepAvg  = avg(progress?.sleep_log, 'hours');
  const moodAvg   = avg(progress?.mood_log, 'score');
  const exCount   = progress?.exercise_count || 0;
  const badgeList = progress?.badges || [];

  return (
    <div style={{ padding: '0 0 32px' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 16px' }}>
        ← {nl ? 'Terug' : 'Back'}
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{ width: 54, height: 54, borderRadius: '50%', background: `${_T.accent}20`, border: `2px solid ${_T.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
          {client.avatar || '👤'}
        </div>
        <div>
          <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{client.name}</div>
          <div style={{ color: C.silver, fontSize: 12 }}>{client.email}</div>
          <div style={{ color: C.silver2, fontSize: 11, marginTop: 2 }}>
            {nl ? 'Klant sinds' : 'Client since'} {new Date(client.joined_at || Date.now()).toLocaleDateString()}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div>
      ) : (
        <>
          {/* Wellness snapshot */}
          <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>
              {nl ? 'WELLNESS OVERZICHT' : 'WELLNESS SNAPSHOT'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <StatPill label={nl ? 'Slaap gem.' : 'Sleep avg'} value={sleepAvg ? `${sleepAvg}h` : null} color="#5B9ED0" />
              <StatPill label={nl ? 'Stemming' : 'Mood avg'} value={moodAvg ? `${moodAvg}/5` : null} color="#2A9E82" />
              <StatPill label={nl ? 'Sessies' : 'Sessions'} value={exCount} color="#D4873A" />
              <StatPill label={nl ? 'Badges' : 'Badges'} value={badgeList.length} color="#C8A84B" />
            </div>
          </div>

          {/* Sleep chart (simple bars) */}
          {progress?.sleep_log?.length > 0 && (
            <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ fontSize: 11, color: C.silver, fontWeight: 600, marginBottom: 10 }}>
                😴 {nl ? 'Slaap (laatste 7 dagen)' : 'Sleep (last 7 days)'}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 48 }}>
                {progress.sleep_log.slice(-7).map((s, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <div style={{ width: '100%', background: '#5B9ED0', borderRadius: 3, height: `${Math.max(4, (s.hours / 10) * 48)}px` }} />
                    <div style={{ fontSize: 8, color: C.silver2 }}>{s.hours}h</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mood log */}
          {progress?.mood_log?.length > 0 && (
            <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ fontSize: 11, color: C.silver, fontWeight: 600, marginBottom: 10 }}>
                💚 {nl ? 'Stemming (laatste 7 dagen)' : 'Mood (last 7 days)'}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 40 }}>
                {progress.mood_log.slice(-7).map((m, i) => (
                  <div key={i} style={{ flex: 1, background: '#2A9E82', borderRadius: 3, height: `${(m.score / 5) * 40}px` }} />
                ))}
              </div>
            </div>
          )}

          {/* Active programs */}
          {progress?.programs?.length > 0 && (
            <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ fontSize: 11, color: C.silver, fontWeight: 600, marginBottom: 10 }}>
                🏃 {nl ? "Actieve programma's" : 'Active Programs'}
              </div>
              {progress.programs.map((p, i) => {
                const pct = Math.round(((p.completed_tasks?.length || 0) / (p.total_tasks || 1)) * 100);
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: C.white }}>{p.title}</span>
                      <span style={{ color: _T.accent }}>{pct}%</span>
                    </div>
                    <div style={{ background: _T.bg4, borderRadius: 4, height: 6 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Badges */}
          {badgeList.length > 0 && (
            <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, border: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ fontSize: 11, color: C.silver, fontWeight: 600, marginBottom: 10 }}>
                🏅 {nl ? 'Badges' : 'Badges'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {badgeList.map((b, i) => (
                  <div key={i} style={{ background: `${_T.accent}15`, border: `1px solid ${_T.accent}33`, borderRadius: 8, padding: '4px 10px', fontSize: 12, color: _T.accent }}>
                    {b.icon} {nl ? b.labelNL : b.labelEN}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function CoachClientsTab({ coachId, tier, lang }) {
  const nl = lang === 'nl';
  const limit = TIER_LIMITS[tier] || 15;

  const [clients, setClients]     = useState([]);
  const [invites, setInvites]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviting, setInviting]   = useState(false);
  const [copied, setCopied]       = useState(null);
  const [search, setSearch]       = useState('');

  const load = useCallback(() => {
    Promise.all([
      getClients(coachId).catch(() => []),
      getCoachInvites(coachId).catch(() => []),
    ]).then(([c, i]) => {
      if (Array.isArray(c)) setClients(c);
      if (Array.isArray(i)) setInvites(i);
      setLoading(false);
    });
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    setInviting(true); setInviteMsg(null);
    const d = await createCoachInvite(coachId, { email: inviteEmail.trim() }).catch(() => ({ error: 'Failed' }));
    if (d.invite_url || d.token) {
      setInviteMsg({ ok: true, url: d.invite_url || `${window.location.origin}/coach-invite/${d.token}` });
      setInviteEmail('');
      load();
    } else {
      setInviteMsg({ ok: false, text: d.error || (nl ? 'Mislukt' : 'Failed') });
    }
    setInviting(false);
  };

  const handleRemove = async (uid, name) => {
    if (!window.confirm(nl ? `${name} verwijderen als klant?` : `Remove ${name} as a client?`)) return;
    await removeClient(coachId, uid).catch(() => {});
    load();
  };

  const handleCopyLink = (url, id) => {
    navigator.clipboard.writeText(url).then(() => { setCopied(id); setTimeout(() => setCopied(null), 2000); }).catch(() => {});
  };

  if (selected) return <ClientDetailView client={selected} coachId={coachId} nl={nl} onBack={() => setSelected(null)} />;

  const filtered = clients.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()));
  const atLimit  = clients.length >= limit;

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Klanten' : 'Clients'}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
            {clients.length}{limit !== Infinity ? `/${limit}` : ''} {nl ? 'actieve klanten' : 'active clients'}
          </div>
        </div>
        <button onClick={() => setShowInvite(v => !v)} disabled={atLimit}
          style={{ background: atLimit ? _T.bg4 : `${_T.accent}20`, border: `1px solid ${atLimit ? _T.cardBorderBot : _T.accent + '55'}`, borderRadius: 10, color: atLimit ? C.silver2 : _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: atLimit ? 'not-allowed' : 'pointer' }}>
          + {nl ? 'Uitnodigen' : 'Invite'}
        </button>
      </div>

      {atLimit && (
        <div style={{ background: '#C8A84B18', border: '1px solid #C8A84B44', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#C8A84B', marginBottom: 12 }}>
          {nl ? `Limiet bereikt (${limit} klanten). Upgrade voor meer.` : `Limit reached (${limit} clients). Upgrade for more.`}
        </div>
      )}

      {/* Invite panel */}
      {showInvite && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            {nl ? 'Klant uitnodigen' : 'Invite a client'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder={nl ? 'E-mailadres (optioneel)' : 'Email address (optional)'}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none' }} />
            <button onClick={handleInvite} disabled={inviting}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '0 16px', cursor: 'pointer', flexShrink: 0 }}>
              {inviting ? '...' : (nl ? 'Genereer link' : 'Generate link')}
            </button>
          </div>
          {inviteMsg && (
            <div style={{ marginTop: 10 }}>
              {inviteMsg.ok ? (
                <div style={{ background: '#4AC87A15', border: '1px solid #4AC87A44', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ color: '#4AC87A', fontSize: 12, marginBottom: 6 }}>✓ {nl ? 'Link aangemaakt' : 'Link created'}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ flex: 1, fontSize: 10, color: C.silver2, wordBreak: 'break-all' }}>{inviteMsg.url}</div>
                    <button onClick={() => handleCopyLink(inviteMsg.url, 'new')}
                      style={{ background: copied === 'new' ? '#4AC87A20' : _T.bg4, border: `1px solid ${copied === 'new' ? '#4AC87A55' : _T.cardBorderBot}`, borderRadius: 8, color: copied === 'new' ? '#4AC87A' : C.silver, fontSize: 11, padding: '5px 10px', cursor: 'pointer', flexShrink: 0 }}>
                      {copied === 'new' ? '✓' : (nl ? 'Kopieer' : 'Copy')}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#C05050', fontSize: 12 }}>⚠ {inviteMsg.text}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pending invites */}
      {invites.length > 0 && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 8 }}>{nl ? 'OPENSTAANDE UITNODIGINGEN' : 'PENDING INVITES'}</div>
          {invites.map(inv => (
            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${_T.cardBorderBot}` }}>
              <div>
                <div style={{ color: C.white, fontSize: 12 }}>{inv.email || (nl ? 'Geen e-mail' : 'No email')}</div>
                <div style={{ color: C.silver2, fontSize: 10 }}>{nl ? 'Aangemaakt' : 'Created'} {new Date(inv.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handleCopyLink(inv.invite_url, inv.id)}
                  style={{ background: copied === inv.id ? '#4AC87A20' : _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 7, color: copied === inv.id ? '#4AC87A' : C.silver, fontSize: 10, padding: '4px 8px', cursor: 'pointer' }}>
                  {copied === inv.id ? '✓' : (nl ? 'Link' : 'Link')}
                </button>
                <button onClick={() => deleteCoachInvite(coachId, inv.id).then(load)}
                  style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer', padding: '0 4px' }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      {clients.length > 4 && (
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={nl ? 'Zoek klant...' : 'Search client...'}
          style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none', marginBottom: 10, boxSizing: 'border-box' }} />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>{nl ? 'Nog geen klanten' : 'No clients yet'}</div>
          <div style={{ fontSize: 12, color: C.silver2 }}>{nl ? 'Stuur een uitnodigingslink om te beginnen' : 'Send an invite link to get started'}</div>
        </div>
      ) : (
        filtered.map(c => (
          <div key={c.user_id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${_T.accent}20`, border: `2px solid ${_T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                {c.avatar || '👤'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{c.name}</div>
                <div style={{ color: C.silver, fontSize: 11 }}>{c.email}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setSelected(c)}
                  style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
                  {nl ? 'Bekijk' : 'View'}
                </button>
                <button onClick={() => handleRemove(c.user_id, c.name)}
                  style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>×</button>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', background: _T.bg4, borderRadius: 10, padding: '10px 6px' }}>
              <StatPill label={nl ? 'Sessies' : 'Sessions'} value={c.session_count || 0} />
              <StatPill label={nl ? 'Slaap' : 'Sleep'} value={c.avg_sleep ? `${c.avg_sleep}h` : null} color="#5B9ED0" />
              <StatPill label={nl ? 'Stemming' : 'Mood'} value={c.avg_mood ? `${c.avg_mood}/5` : null} color="#2A9E82" />
              <StatPill label={nl ? 'Laatste sessie' : 'Last seen'} value={c.last_session ? new Date(c.last_session).toLocaleDateString('nl', { day: '2-digit', month: 'short' }) : null} color={C.silver} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
